/**
 * Protected Route Component
 *
 * Wraps dashboard routes to ensure the visitor is:
 *  1. Authenticated via server-side session (CIAM login)
 *  2. Has a usable subscription (status === 'active' or 'past_due' for grace)
 *
 * Flow:
 *  1. Wait for SubscriptionContext loading to settle
 *  2. Unauthenticated → redirect to /auth/login (preserves dev login backdoor)
 *  3. No active sub   → redirect to /get-access (they need to subscribe)
 *  4. All gates pass  → render children
 *
 * There used to be a third gate and a `requireAres` prop, bouncing Athena-tier
 * users off Ares-only pages. Under the single tier every active subscription
 * includes every page, so "has Ares" and "has an active subscription" ask the
 * same question — gate 2 already covers it. The prop was removed from all 31
 * call sites rather than left as a no-op that implies gating still happens.
 *
 * NOTE on DTA-only flow: the "end users access dashboard only via DTA" constraint
 * is enforced at the MARKETING layer (no public links to /dashboard) and the checkout
 * success URL pointing at /welcome instead of /dashboard. ProtectedRoute itself does
 * NOT bounce paid web visitors to /welcome — that would break the /dashboard/login
 * dev backdoor. Unlisted URLs are fair game for team testing.
 */

import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { API_BASE_URL } from '../../../config/api';

function ProtectedRoute({ children, requireAdditionalAuth = false }) {
  const location = useLocation();
  const navigate = useNavigate();

  const { loading, authenticated, subscription } = useSubscription();

  // 'active' and 'past_due' both grant access (past_due is the Stripe Smart Retries
  // grace window). 'canceled', 'none', and anything else deny.
  const hasActiveAccess =
    subscription &&
    (subscription.status === 'active' || subscription.status === 'past_due');

  useEffect(() => {
    if (loading) return;

    // Gate 1: not authenticated → CIAM login (dev backdoor at /dashboard/login also routes here)
    if (!authenticated) {
      const returnUrl = encodeURIComponent(location.pathname + location.search);
      console.log('[ProtectedRoute] Not authenticated, redirecting to login. Return URL:', returnUrl);
      window.location.href = `${API_BASE_URL}/auth/login?returnUrl=${returnUrl}`;
      return;
    }

    // Gate 2: no active/past_due subscription → plan selection
    if (!hasActiveAccess) {
      console.log('[ProtectedRoute] No active subscription, redirecting to /get-access. Status:', subscription?.status);
      navigate('/get-access');
      return;
    }

    // Gate 3: sensitive pages — TODO: implement additional auth challenge if needed
    if (requireAdditionalAuth) {
      console.log('[ProtectedRoute] Additional auth required (not yet enforced)');
    }
  }, [loading, authenticated, subscription, hasActiveAccess, requireAdditionalAuth, location, navigate]);

  // Render states

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nuvola-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nuvola-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  if (!hasActiveAccess) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nuvola-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Redirecting to subscription...</p>
        </div>
      </div>
    );
  }

  return children;
}

export default ProtectedRoute;
